import IProperty from './constants/IProperty';
import {
  IIProperty,
  IApiaField,
  IApiaForm,
  TApiaField,
  TApiaHTMLElement,
} from './types';
import Field from './constants/Field';
import {
  ButtonField,
  CaptchaField,
  CheckField,
  DataGridField,
  FileUploaderField,
  HiddenField,
  ImageField,
  InputDatePickerField,
  InputField,
  PasswordField,
  LinkField,
  MultipleField,
  RadioField,
  SelectField,
  TextAreaField,
  TextField,
  TreeField,
  EditorField,
} from './fields';
import propsStore from '../store/propsStore/propsStore';
import { apiaFormMethods } from '../components/apia/execution/page/ApiaForm';

export default class ApiaForm implements IApiaForm {
  fldArr: IApiaField[];

  formElement: HTMLElement;

  iProperty: IIProperty;

  frmId: string | null;

  name: string | null;

  title: string | null;

  type: string | null;

  constructor(form: HTMLElement) {
    this.formElement = form;
    this.frmId = form.getAttribute('id');
    this.name = form.getAttribute('data-form-name');
    this.title = form.getAttribute('data-form-title');
    this.type = form.getAttribute('data-form-type');
    this.fldArr = [];
    this.iProperty = IProperty;
  }

  #createNewFieldFromHtml(fld: TApiaHTMLElement): IApiaField | null {
    if (fld !== null) {
      return this.#createNewField(fld, this);
    }
    return null;
  }

  #createNewField = (
    fld: TApiaHTMLElement,
    form: ApiaForm,
  ): IApiaField | null => {
    const { type, valueType } = fld.dataset;
    switch (type) {
      case Field.TYPE_INPUT:
        if (valueType === 'D') {
          return new InputDatePickerField(fld as HTMLInputElement, form);
        }
        return new InputField(fld as HTMLInputElement, form);
      case Field.TYPE_PASSWORD:
        return new PasswordField(fld as HTMLInputElement, form);
      case Field.TYPE_SELECT:
        return new SelectField(fld as HTMLSelectElement, form);
      case Field.TYPE_RADIO:
        return new RadioField(fld as HTMLDivElement, form);
      case Field.TYPE_CHECK:
        return new CheckField(fld as HTMLInputElement, form);
      case Field.TYPE_BUTTON:
        return new ButtonField(fld as HTMLButtonElement, form);
      case Field.TYPE_MULTIPLE:
        return new MultipleField(fld as HTMLDivElement, form);
      case Field.TYPE_HIDDEN:
        return new HiddenField(fld as HTMLInputElement, form);
      case Field.TYPE_AREA:
        return new TextAreaField(fld as HTMLTextAreaElement, form);
      case Field.TYPE_GRID:
        return new DataGridField(fld as HTMLDivElement, form);
      case Field.TYPE_IMAGE:
        return new ImageField(fld as HTMLImageElement, form);
      case Field.TYPE_HREF:
        return new LinkField(fld as HTMLAnchorElement, form);
      case Field.TYPE_LABEL:
        return new TextField(fld as HTMLDivElement, form);
      case Field.TYPE_TITLE:
        return new TextField(fld as HTMLDivElement, form);
      case Field.TYPE_FILE:
        return new FileUploaderField(fld as HTMLInputElement, form);
      case Field.TYPE_CAPTCHA:
        return new CaptchaField(fld as HTMLDivElement, form);
      case Field.TYPE_TREE:
        return new TreeField(fld as HTMLDivElement, form);
      case Field.TYPE_EDITOR:
        return new EditorField(fld as HTMLDivElement, form);
        break;
      default:
        break;
    }
    return null;
  };

  getFieldInstance(fld: TApiaHTMLElement): IApiaField | null {
    const actualFieldElement = fld.closest<HTMLElement>('[data-idx]');

    if (actualFieldElement) {
      const i: number = this.fldArr.findIndex(
        (item: IApiaField) =>
          item.fldId === actualFieldElement.getAttribute('data-idx'),
      );
      if (i > -1) {
        if (document.body.contains(this.fldArr[i].field)) {
          return this.fldArr[i];
        }
        // not in body, remove from array.
        this.fldArr.splice(i, 1);
      }
      // not in array, create new one and add.
      const newField = this.#createNewFieldFromHtml(
        actualFieldElement as TApiaHTMLElement,
      );
      if (newField !== null) {
        this.fldArr.push(newField);
        return newField;
      }
    }
    return null;
  }

  getField<T extends IApiaField>(fldName: string, index?: number): T | null {
    const query =
      index !== undefined
        ? `[name="${fldName}" i][data-idx="${index}"], [data-name="${fldName}" i][data-idx="${index}"], #${fldName}[data-idx="${index}"]`
        : `[name="${fldName}" i], [data-name="${fldName}" i], #${fldName}`;
    const fld = this.formElement.querySelector(query) as TApiaHTMLElement;

    if (fld) {
      const field = this.getFieldInstance(fld);
      if (field !== null) {
        return field as T;
      }
    }

    return null;
  }

  getFieldById(fldId: string): TApiaField | null {
    const fld = this.formElement.querySelector<TApiaHTMLElement>(
      `[data-type="select"][data-id="${fldId}"], #${fldId}`,
    );
    if (fld) {
      const field = this.getFieldInstance(fld);
      if (field !== null) {
        return field;
      }
    }
    return null;
  }

  getAllFields(): IApiaField[] {
    const props = propsStore.getAllFields();
    const formFields: IApiaField[] = [];
    const frmIdNumber = this.formElement.id.split('_')[1];
    Object.keys(props).forEach((key) => {
      const isFieldInForm = key.includes(frmIdNumber);
      if (isFieldInForm && key !== this.formElement.id) {
        const fieldElement = document.querySelector(
          `[data-type="select"][data-id="${key}"], #${key}`,
        ) as TApiaHTMLElement;
        if (
          fieldElement !== null &&
          fieldElement.dataset.fieldInGrid === 'false'
        ) {
          const field = this.getFieldInstance(fieldElement);
          if (field) {
            formFields.push(field);
          }
        }
      }
    });
    return formFields;
  }

  getAttField(attId: string | number): IApiaField | IApiaField[] | null {
    const fld = this.formElement.querySelectorAll(`[data-att-id="${attId}"]`);
    if (fld.length === 1) {
      // find instance or return a new one searching attid
      const field = this.getFieldInstance(fld[0] as TApiaHTMLElement);
      if (field !== null) return field;
    } else {
      const attFlds: IApiaField[] = [];
      fld.forEach((f) => {
        const attFld = this.getFieldInstance(f as TApiaHTMLElement);
        attFlds.push(attFld as IApiaField);
      });
      return attFlds;
    }

    return null;
  }

  getFormTitle(): string | null {
    return this.title;
  }

  getFormName(): string | null {
    return this.name;
  }

  setProperty(name: string, value: string | number | boolean): void {
    const hasProp = Object.keys(this.iProperty).some(
      (k) => this.iProperty[k as keyof IIProperty] === name,
    );
    if (hasProp === true) {
      if (name === this.iProperty.PROPERTY_FORM_INVISIBLE) {
        throw new Error(
          'Apia JSApi Error: The property "PROPERTY_FORM_INVISIBLE" can only be set in a JAVA business class.\nUse PROPERTY_FORM_HIDDEN instead.',
        );
      } else if (name === this.iProperty.PROPERTY_FORM_TAB) {
        throw new Error(
          'Apia JSApi Error: The property "PROPERTY_FORM_TAB" can only be set in a JAVA business class.',
        );
      } else if (name === this.iProperty.PROPERTY_FORM_DONT_FIRE) {
        throw new Error(
          'Apia JSApi Error: The property "PROPERTY_FORM_DONT_FIRE" can only be set in a JAVA business class.',
        );
      } else if (name === this.iProperty.PROPERTY_FORM_READONLY) {
        throw new Error(
          'Apia JSApi Error: The property "PROPERTY_FORM_READONLY" can only be set in a JAVA business class.',
        );
      } else if (name !== null && value !== null && this.frmId !== null) {
        propsStore.updateField(this.frmId, { [name]: value });
      }
    } else {
      throw new Error(
        `Apia JSApi Error: Form does not accept the property ${name}.`,
      );
    }
  }

  getProperty(name: string): unknown {
    if (document.body.contains(this.formElement)) {
      if (typeof this.frmId === 'string' && name !== undefined) {
        const props = propsStore.getFieldProps(this.frmId);
        if (props) {
          return props[name as keyof typeof props];
        }
      }
    }
    return null;
  }

  closeForm(): void {
    this.setProperty(this.iProperty.PROPERTY_FORM_CLOSED, true);
  }

  openForm(): void {
    this.setProperty(this.iProperty.PROPERTY_FORM_CLOSED, false);
  }

  clearForm(clearReadonlyFields: boolean, deleteGridsRows: boolean): void {
    const fields = this.getAllFields();
    fields.forEach((fld: IApiaField) => {
      if (
        clearReadonlyFields ||
        (!fld.getProperty('readonly') && !fld.getProperty('readOnly'))
      ) {
        if (
          fld.fldType !== Field.TYPE_BUTTON &&
          fld.fldType !== Field.TYPE_GRID
        )
          fld.clearValue();
        if (fld.fldType === Field.TYPE_GRID) {
          if (deleteGridsRows) (fld as DataGridField).deleteGrid();
          else fld.clearGrid();
        }
      }
    });
  }

  getFieldColumn(fldName: string) {
    let fields = [
      ...this.formElement.querySelectorAll(
        `[name="${fldName}" i][data-field-in-grid="true"]`,
      ),
    ];

    if (fields.length === 0) {
      fields = [
        ...this.formElement.querySelectorAll(
          `[data-name="${fldName}" i][data-field-in-grid="true"]`,
        ),
      ];
    }

    return fields.map((field) =>
      this.getFieldInstance(field as TApiaHTMLElement),
    );
  }

  hideModal() {
    if (this.frmId) {
      apiaFormMethods.showAsModal(this.frmId, false);
    }
  }

  showAsModal() {
    if (this.frmId) {
      apiaFormMethods.showAsModal(this.frmId, true);
    }
  }
}
